import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import { UploadDialog } from "../components/UploadDialog";
import { AISearchDialog } from "../components/AISearch";
import { MediaCard } from "../components/MediaCard";
import { GhostTiles } from "../components/GhostTiles";
import { ActivityFeed } from "../components/ActivityFeed";
import { PremiumBadge, PremiumLock } from "../components/PremiumLock";
import { BillingConfig } from "../components/BillingConfig";
import { UpgradeRequests } from "../components/UpgradeRequests";
import { api, formatBytes } from "../lib/api";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Film, Image as ImageIcon, Music, FileText, HardDrive, Activity, Users, ShieldCheck } from "lucide-react";

const KIND_META = {
  video: { icon: Film, label: "Videos", color: "#E60000" },
  image: { icon: ImageIcon, label: "Images", color: "#0A0A0A" },
  audio: { icon: Music, label: "Audio", color: "#0A0A0A" },
  document: { icon: FileText, label: "Docs", color: "#0A0A0A" },
};

export default function Workspace() {
  const { user, isPremium } = useAuth();
  const [stats, setStats] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSeed, setSearchSeed] = useState("");
  const [billingOpen, setBillingOpen] = useState(false);

  const load = async () => {
    try { const res = await api.get("/workspace/stats"); setStats(res.data); }
    catch {}
  };
  useEffect(() => { load(); }, []);

  const usage = stats ? Math.min(100, (stats.total_size / stats.quota) * 100) : 0;
  const handleGhostPick = (p) => { setSearchSeed(p); setSearchOpen(true); };
  const isOwner = user?.role === "owner";

  return (
    <div className="min-h-screen bg-white text-black" data-testid="workspace-page">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onUploadOpen={() => setUploadOpen(true)} />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-3">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Squad</div>
          <PremiumBadge user={user} />
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.9] mt-2">
          {user?.name?.split(" ")[0]}'s workspace.
        </h1>

        {/* Quota */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 border border-black/15 p-8" data-testid="storage-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-black/50">
              <HardDrive className="w-3.5 h-3.5" /> Storage usage
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="font-display text-5xl font-black tracking-tighter">
                {stats ? formatBytes(stats.total_size) : "—"}
              </div>
              <div className="text-black/50 text-sm">/ {stats ? formatBytes(stats.quota) : "5 GB"}</div>
            </div>
            <Progress value={usage} className="mt-4 h-1.5 rounded-none" />
            <div className="mt-2 flex justify-between text-xs text-black/50 font-mono">
              <span>{stats?.total_count || 0} files</span>
              <span>{usage.toFixed(1)}% used</span>
            </div>
          </div>
          <div className="border border-black/15 p-8 bg-black text-white" data-testid="ai-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#E60000]">
              <Activity className="w-3.5 h-3.5" /> Intelligence
            </div>
            <div className="mt-3 font-heading font-black text-2xl tracking-tight">Claude Sonnet 4.5</div>
            <div className="text-white/60 text-sm mt-2">Natural-language search ready. Hit ⌘K from anywhere.</div>
          </div>
        </div>

        {!isPremium && <PremiumLock id="upgrade" feature="Squad premium" className="mt-10" />}

        {/* Owner billing controls */}
        {isOwner && (
          <div className="mt-10 grid lg:grid-cols-2 gap-6" data-testid="owner-billing-section">
            <div className="border border-black/15 p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#E60000]">
                <ShieldCheck className="w-3.5 h-3.5" /> Chase QuickAccept · Pay-by-Link
              </div>
              <h3 className="mt-2 font-heading font-black text-xl tracking-tight">Configure payments</h3>
              <p className="mt-2 text-sm text-black/55">
                Paste your Chase QuickAccept Pay-by-Link URL to let members pay through Chase.
                Global Relocation LLC dba Monster Squad.
              </p>
              <Button onClick={() => setBillingOpen(true)} className="mt-4 rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" data-testid="open-billing-config">
                <ShieldCheck className="w-4 h-4 mr-2" /> Configure pay link
              </Button>
            </div>
            <UpgradeRequests ownerView onChange={load} />
          </div>
        )}

        {/* Member: their own request status */}
        {!isOwner && <div className="mt-10"><UpgradeRequests /></div>}

        {/* Ghost prompts + Activity */}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <GhostTiles onPick={handleGhostPick} />
          </div>
          <div className="border-l border-black/10 pl-8" data-testid="activity-side">
            <ActivityFeed limit={12} />
          </div>
        </div>

        {/* By kind */}
        <div className="mt-10 grid md:grid-cols-4 gap-3" data-testid="kinds-grid">
          {Object.entries(KIND_META).map(([k, meta]) => {
            const data = stats?.by_kind?.[k] || { count: 0, size: 0 };
            const Icon = meta.icon;
            return (
              <div key={k} className="border border-black/15 p-6 hover:bg-black hover:text-white transition-colors group" data-testid={`kind-${k}`}>
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <div className="mt-4 font-heading font-black text-3xl tracking-tighter">{data.count}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-black/50 group-hover:text-white/60 font-bold mt-1">
                  {meta.label}
                </div>
                <div className="text-xs text-black/50 group-hover:text-white/60 mt-1 font-mono">{formatBytes(data.size)}</div>
              </div>
            );
          })}
        </div>

        {/* Recent */}
        <div className="mt-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-black text-3xl tracking-tight">Recent uploads</h2>
            <span className="flex items-center gap-2 text-xs text-black/50">
              <Users className="w-4 h-4" /> Solo squad
            </span>
          </div>
          {stats?.recent?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {stats.recent.map((m, i) => <MediaCard key={m.id} media={m} index={i} />)}
            </div>
          ) : (
            <div className="border border-dashed border-black/15 p-10 text-center text-black/50 text-sm" data-testid="recent-empty">
              No activity yet. Upload your first media to see it here.
            </div>
          )}
        </div>
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <AISearchDialog open={searchOpen} onOpenChange={(v) => { setSearchOpen(v); if (!v) setSearchSeed(""); }} initialQuery={searchSeed} />
      <BillingConfig open={billingOpen} onOpenChange={setBillingOpen} onSaved={load} />
    </div>
  );
}
